import { useEffect, useState } from "react";
import api, { fmtErr } from "../lib/api";
import { Card, Section, TH, TD, Empty, Button, Badge, Label } from "../components/ui";
import { ShieldCheck, FlaskConical, Play, ExternalLink } from "lucide-react";
import { toast } from "sonner";

// ISO week helpers (Monday = first day of week, ISO-8601 like Spain/EU)
const isoWeekRange = (year, week) => {
  // Jan 4 is always in week 1 (ISO definition)
  const jan4 = new Date(Date.UTC(year, 0, 4));
  const dow = (jan4.getUTCDay() + 6) % 7; // Mon=0
  const week1Monday = new Date(jan4);
  week1Monday.setUTCDate(jan4.getUTCDate() - dow);
  const start = new Date(week1Monday);
  start.setUTCDate(week1Monday.getUTCDate() + (week - 1) * 7);
  const end = new Date(start);
  end.setUTCDate(start.getUTCDate() + 6);
  const fmt = (d) => `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}-${String(d.getUTCDate()).padStart(2, "0")}`;
  return { from: fmt(start), to: fmt(end) };
};

const weeksInYear = (year) => {
  // ISO weeks count: 52 or 53. Check if Dec 28 is in week 52 or 53.
  const dec28 = new Date(Date.UTC(year, 11, 28));
  const dow = (dec28.getUTCDay() + 6) % 7;
  const jan4 = new Date(Date.UTC(year, 0, 4));
  const dow2 = (jan4.getUTCDay() + 6) % 7;
  const week1Monday = new Date(jan4);
  week1Monday.setUTCDate(jan4.getUTCDate() - dow2);
  const target = new Date(dec28);
  target.setUTCDate(dec28.getUTCDate() - dow);
  const diff = (target - week1Monday) / (7 * 24 * 60 * 60 * 1000);
  return Math.round(diff) + 1;
};

// Returns the ISO weeks (1..N) whose [from,to] range intersects the given month
const weeksOfMonth = (year, month) => {
  const monthStart = new Date(Date.UTC(year, month - 1, 1));
  const monthEnd = new Date(Date.UTC(year, month, 0)); // last day of month
  const total = weeksInYear(year);
  const out = [];
  for (let w = 1; w <= total; w++) {
    const { from, to } = isoWeekRange(year, w);
    const fromD = new Date(from + "T00:00:00Z");
    const toD = new Date(to + "T00:00:00Z");
    if (toD >= monthStart && fromD <= monthEnd) out.push(w);
  }
  return out;
};

export default function VeriFactu() {
  const [companies, setCompanies] = useState([]);
  const [companyId, setCompanyId] = useState("");
  const [busy, setBusy] = useState(null);
  const [mappings, setMappings] = useState([]);

  const currentYear = new Date().getFullYear();
  const years = [];
  for (let y = currentYear; y >= 2024; y--) years.push(y);

  const [periodMode, setPeriodMode] = useState("month");
  const [year, setYear] = useState(currentYear);
  const [quarter, setQuarter] = useState(1);
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [week, setWeek] = useState(1);

  useEffect(() => {
    api.get("/companies").then(({ data }) => {
      setCompanies(data);
      if (data.length && !companyId) setCompanyId(data[0].id);
    });
  }, []); // eslint-disable-line

  const loadMappings = (cid) => {
    if (!cid) { setMappings([]); return; }
    api.get(`/verifactu/mappings?company_id=${cid}&limit=200`)
      .then(({ data }) => setMappings(data))
      .catch(() => setMappings([]));
  };
  useEffect(() => { loadMappings(companyId); }, [companyId]);

  const range = (() => {
    const pad = (n) => String(n).padStart(2, "0");
    const lastDay = (y, m) => new Date(y, m, 0).getDate();
    if (periodMode === "none") return { date_from: null, date_to: null };
    if (periodMode === "year") return { date_from: `${year}-01-01`, date_to: `${year}-12-31` };
    if (periodMode === "quarter") {
      const sm = (quarter - 1) * 3 + 1;
      const em = sm + 2;
      return { date_from: `${year}-${pad(sm)}-01`, date_to: `${year}-${pad(em)}-${pad(lastDay(year, em))}` };
    }
    if (periodMode === "month") {
      return { date_from: `${year}-${pad(month)}-01`, date_to: `${year}-${pad(month)}-${pad(lastDay(year, month))}` };
    }
    if (periodMode === "week") {
      const { from, to } = isoWeekRange(year, week);
      return { date_from: from, date_to: to };
    }
    return { date_from: null, date_to: null };
  })();

  const start = async (mode) => {
    if (!companyId) { toast.error("Seleziona una società"); return; }
    if (mode === "real" && periodMode === "none") {
      const ok = window.confirm("⚠️ ATTENZIONE\n\nStai per emettere fatture VeriFactu (definitive, inviate ad AEAT) per TUTTE le fatture Zoho senza filtro periodo.\n\nÈ una operazione FISCALMENTE RILEVANTE. Procedere?");
      if (!ok) return;
    }
    setBusy(mode);
    try {
      const body = { entity: "invoices_verifactu", mode };
      if (range.date_from) body.date_from = range.date_from;
      if (range.date_to) body.date_to = range.date_to;
      const { data } = await api.post(`/sync/${companyId}/start`, body);
      toast.success(`Job VeriFactu avviato (id ${data.id.slice(0, 8)}…)`);
      setTimeout(() => loadMappings(companyId), 4000);
    } catch (e) {
      toast.error(fmtErr(e.response?.data?.detail) || e.message);
    } finally { setBusy(null); }
  };

  return (
    <div className="space-y-6" data-testid="verifactu-page">
      <Section
        title={<span className="flex items-center gap-2"><ShieldCheck size={20} /> Sync VeriFactu</span>}
        subtitle="Emissione fiscale via FacturaDirecta con generazione automatica QR + hash AEAT. Numerazione assegnata da FD. Flusso indipendente dal sync 'Archivio'."
      >
        <Card className="p-5 space-y-4">
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <Label>Società</Label>
              <select className="h-9 px-3 rounded-md border border-border-default text-sm bg-surface"
                      value={companyId} onChange={(e) => setCompanyId(e.target.value)}
                      data-testid="vf-company-select">
                {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div>
              <Label>Periodo</Label>
              <select className="h-9 px-3 rounded-md border border-border-default text-sm bg-surface"
                      value={periodMode} onChange={(e) => setPeriodMode(e.target.value)}
                      data-testid="vf-period-mode">
                <option value="week">Settimana</option>
                <option value="month">Mese</option>
                <option value="quarter">Trimestre</option>
                <option value="year">Anno</option>
                <option value="none">Nessun filtro</option>
              </select>
            </div>
            {periodMode !== "none" && (
              <div>
                <Label>Anno</Label>
                <select className="h-9 px-3 rounded-md border border-border-default text-sm bg-surface"
                        value={year}
                        onChange={(e) => {
                          const y = parseInt(e.target.value);
                          setYear(y);
                          if (periodMode === "week") {
                            const wks = weeksOfMonth(y, month);
                            if (wks.length && !wks.includes(week)) setWeek(wks[0]);
                          }
                        }}
                        data-testid="vf-period-year">
                  {years.map((y) => <option key={y} value={y}>{y}</option>)}
                </select>
              </div>
            )}
            {periodMode === "quarter" && (
              <div>
                <Label>Trimestre</Label>
                <select className="h-9 px-3 rounded-md border border-border-default text-sm bg-surface"
                        value={quarter} onChange={(e) => setQuarter(parseInt(e.target.value))}
                        data-testid="vf-period-quarter">
                  <option value={1}>Q1 (Gen-Mar)</option>
                  <option value={2}>Q2 (Apr-Giu)</option>
                  <option value={3}>Q3 (Lug-Set)</option>
                  <option value={4}>Q4 (Ott-Dic)</option>
                </select>
              </div>
            )}
            {periodMode === "month" && (
              <div>
                <Label>Mese</Label>
                <select className="h-9 px-3 rounded-md border border-border-default text-sm bg-surface"
                        value={month} onChange={(e) => setMonth(parseInt(e.target.value))}
                        data-testid="vf-period-month">
                  {["Gen","Feb","Mar","Apr","Mag","Giu","Lug","Ago","Set","Ott","Nov","Dic"].map((m, i) =>
                    <option key={i + 1} value={i + 1}>{i + 1} — {m}</option>)}
                </select>
              </div>
            )}
            {periodMode === "week" && (
              <>
                <div>
                  <Label>Mese</Label>
                  <select className="h-9 px-3 rounded-md border border-border-default text-sm bg-surface"
                          value={month}
                          onChange={(e) => {
                            const m = parseInt(e.target.value);
                            setMonth(m);
                            const wks = weeksOfMonth(year, m);
                            if (wks.length && !wks.includes(week)) setWeek(wks[0]);
                          }}
                          data-testid="vf-week-month">
                    {["Gen","Feb","Mar","Apr","Mag","Giu","Lug","Ago","Set","Ott","Nov","Dic"].map((m, i) =>
                      <option key={i + 1} value={i + 1}>{i + 1} — {m}</option>)}
                  </select>
                </div>
                <div>
                  <Label>Settimana ISO</Label>
                  <select className="h-9 px-3 rounded-md border border-border-default text-sm bg-surface"
                          value={week} onChange={(e) => setWeek(parseInt(e.target.value))}
                          data-testid="vf-period-week">
                    {weeksOfMonth(year, month).map((w) => {
                      const r = isoWeekRange(year, w);
                      return <option key={w} value={w}>W{String(w).padStart(2, "0")} — {r.from} → {r.to}</option>;
                    })}
                  </select>
                </div>
              </>
            )}
            <div className="text-xs text-ink-muted font-mono pb-1.5" data-testid="vf-range-preview">
              {range.date_from ? `${range.date_from} → ${range.date_to}` : "tutte le date"}
            </div>
          </div>

          <div className="flex gap-3 pt-2">
            <Button variant="secondary" disabled={busy === "dry_run"} onClick={() => start("dry_run")} data-testid="vf-btn-dry">
              <FlaskConical size={14} className="mr-1.5" /> Dry-Run
            </Button>
            <Button disabled={busy === "real"} onClick={() => start("real")} data-testid="vf-btn-real">
              <Play size={14} className="mr-1.5" /> Emetti VeriFactu
            </Button>
            <Button variant="ghost" onClick={() => loadMappings(companyId)}>
              Aggiorna lista
            </Button>
          </div>
        </Card>
      </Section>

      <Section title="Fatture VeriFactu già emesse">
        <Card className="overflow-hidden">
          <table className="w-full">
            <thead className="border-b border-border-default bg-surface-hover/50">
              <tr>
                <TH>Zoho #</TH>
                <TH>FD serie / num</TH>
                <TH>Stato</TH>
                <TH>Mode</TH>
                <TH>Hash</TH>
                <TH>QR</TH>
                <TH>AEAT confermato</TH>
              </tr>
            </thead>
            <tbody>
              {mappings.map((m) => (
                <tr key={m.id} className="border-b border-border-divider">
                  <TD className="font-mono text-xs">{m.zoho_invoice_number || m.zoho_invoice_id?.slice(0, 8)}</TD>
                  <TD className="font-mono text-xs">{m.fd_series || "—"} / {m.fd_number || "—"}</TD>
                  <TD>{m.verifactu_status ? <Badge tone={m.verifactu_status === "sent" ? "success" : m.verifactu_status === "failed" ? "error" : "info"}>{m.verifactu_status}</Badge> : <Badge>—</Badge>}</TD>
                  <TD className="text-xs">{m.verifactu_mode || "—"}</TD>
                  <TD className="font-mono text-xs truncate max-w-[180px]" title={m.verifactu_hash || ""}>{m.verifactu_hash ? m.verifactu_hash.slice(0, 12) + "…" : "—"}</TD>
                  <TD>
                    {m.verifactu_qr_url ? (
                      <a href={m.verifactu_qr_url} target="_blank" rel="noopener noreferrer"
                         className="text-ink hover:underline text-xs inline-flex items-center gap-1">
                        Apri <ExternalLink size={11} />
                      </a>
                    ) : "—"}
                  </TD>
                  <TD className="text-xs text-ink-muted">{m.verifactu_aeat_confirmed_at ? new Date(m.verifactu_aeat_confirmed_at).toLocaleString("it-IT") : "in attesa"}</TD>
                </tr>
              ))}
              {mappings.length === 0 && <tr><td colSpan={7}><Empty>Nessuna fattura VeriFactu emessa ancora.</Empty></td></tr>}
            </tbody>
          </table>
        </Card>
      </Section>
    </div>
  );
}
